import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Tab,
    Tabs,
    Grid,
    CircularProgress,
    Button,
    ButtonGroup,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Rating,
    Card,
    CardContent,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DownloadIcon from '@mui/icons-material/Download';

// date-fns v4 ESM-Direktimporte brechen unter CRAs node-Resolution — daher
// kleine native Helfer statt format()/subDays() (der DatePicker-Adapter nutzt
// date-fns weiterhin intern).
const toYmd = (d: Date): string => {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
};
const daysAgo = (n: number): Date => new Date(Date.now() - n * 24 * 60 * 60 * 1000);
const isValidDate = (d: Date | null): d is Date =>
    d instanceof Date && !Number.isNaN(d.getTime()) && d.getFullYear() >= 1900;
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import { Title, useNotify, usePermissions } from 'react-admin';
import { LearningProgressDashboard } from '../components/ManagementAnalytics/LearningProgressDashboard';
import { EngagementDashboard } from '../components/ManagementAnalytics/EngagementDashboard';
import { AssessmentDashboard } from '../components/ManagementAnalytics/AssessmentDashboard';
import { FinancialDashboard } from '../components/ManagementAnalytics/FinancialDashboard';
import { CourseDetailDashboard } from '../components/ManagementAnalytics/CourseDetailDashboard';
import { UserDetailDashboard } from '../components/ManagementAnalytics/UserDetailDashboard';
import { CompanySelector } from '../components/ManagementAnalytics/CompanySelector';
import { DepartmentSelector } from '../components/ManagementAnalytics/DepartmentSelector';
import { GroupSelector } from '../components/ManagementAnalytics/GroupSelector';

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

type TabId = 'learning' | 'engagement' | 'assessment' | 'financial' | 'course' | 'user' | 'ratings';
type DateRange = '7' | '30' | '90' | '365' | 'custom';

// Inline ratings dashboard component for admin analytics
const RatingsDashboard: React.FC = () => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [filterCourseId, setFilterCourseId] = useState<string>('');
    const notify = useNotify();

    const loadRatings = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = filterCourseId ? `?courseId=${filterCourseId}` : '';
            const response = await fetch(`${API_URL}/courses/ratings/company${params}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (response.ok) {
                setData(await response.json());
            } else {
                notify('Fehler beim Laden der Bewertungen', { type: 'error' });
            }
        } catch {
            notify('Fehler beim Laden der Bewertungen', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filterCourseId, notify]);

    useEffect(() => { loadRatings(); }, [loadRatings]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
    if (!data) return <Alert severity="info">Keine Bewertungsdaten verfügbar</Alert>;

    return (
        <Box>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="primary">{data.summary.totalRatings}</Typography>
                            <Typography variant="body2" color="text.secondary">Bewertungen gesamt</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <Typography variant="h3" color="primary">
                                    {data.summary.averageRating ?? '–'}
                                </Typography>
                                {data.summary.averageRating && (
                                    <Rating value={data.summary.averageRating} precision={0.1} readOnly size="large" />
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">Durchschnittsbewertung</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="primary">{data.courseSummaries?.length || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Bewertete Kurse</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Course Filter */}
            {data.courseSummaries?.length > 1 && (
                <FormControl size="small" sx={{ mb: 2, minWidth: 250 }}>
                    <InputLabel>Kurs filtern</InputLabel>
                    <Select
                        value={filterCourseId}
                        onChange={(e) => setFilterCourseId(e.target.value)}
                        label="Kurs filtern"
                    >
                        <MenuItem value="">Alle Kurse</MenuItem>
                        {data.courseSummaries.map((cs: any) => (
                            <MenuItem key={cs.courseId} value={cs.courseId}>
                                {cs.courseTitle} ({cs.averageRating}⭐ · {cs.ratingCount})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Course Summaries */}
            {!filterCourseId && data.courseSummaries?.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Bewertungen pro Kurs</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Kurs</TableCell>
                                    <TableCell>Bewertung</TableCell>
                                    <TableCell align="right">Anzahl</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.courseSummaries.map((cs: any) => (
                                    <TableRow key={cs.courseId} hover sx={{ cursor: 'pointer' }} onClick={() => setFilterCourseId(String(cs.courseId))}>
                                        <TableCell>{cs.courseTitle}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Rating value={cs.averageRating} precision={0.1} readOnly size="small" />
                                                <Typography variant="body2">{cs.averageRating}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{cs.ratingCount}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Individual Ratings */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Einzelne Bewertungen</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Kurs</TableCell>
                                <TableCell>Bewertung</TableCell>
                                <TableCell>Kommentar</TableCell>
                                <TableCell>Datum</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.ratings.map((r: any) => (
                                <TableRow key={r.id}>
                                    <TableCell>{r.course_title}</TableCell>
                                    <TableCell>
                                        <Rating value={r.rating} readOnly size="small" />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 400 }}>
                                        {r.comment ? (
                                            <Typography variant="body2">{r.comment}</Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" fontStyle="italic">Kein Kommentar</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {r.created_at ? new Date(r.created_at).toLocaleDateString('de-DE') : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {data.ratings.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography variant="body2" color="text.secondary">Keine Bewertungen vorhanden</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export const AnalyticsPage = () => {
    const notify = useNotify();
    const { permissions } = usePermissions();
    const [activeTab, setActiveTab] = useState<TabId>('learning');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>('30');
    const [customFrom, setCustomFrom] = useState<Date | null>(daysAgo(30));
    const [customTo, setCustomTo] = useState<Date | null>(new Date());
    const [appliedCustom, setAppliedCustom] = useState({ from: toYmd(daysAgo(30)), to: toYmd(new Date()) });
    const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
    const [companyId, setCompanyId] = useState<number | null>(null);
    const [departmentId, setDepartmentId] = useState<number | null>(null);
    const [groupId, setGroupId] = useState<number | null>(null);

    // Report data states
    const [learningData, setLearningData] = useState<any>(null);
    const [engagementData, setEngagementData] = useState<any>(null);
    const [assessmentData, setAssessmentData] = useState<any>(null);
    const [financialData, setFinancialData] = useState<any>(null);

    const isFinanceAllowed = permissions?.includes('ROLE_COMPANY_ADMIN') ||
                             permissions?.includes('ROLE_OPERATOR') ||
                             permissions?.includes('ROLE_SUPER_ADMIN');

    const isOperator = permissions?.includes('ROLE_OPERATOR') ||
                       permissions?.includes('ROLE_SUPER_ADMIN');

    // Build tabs list based on permissions
    const tabs = useMemo(() => {
        const baseTabs: { id: TabId; label: string; icon: React.ReactElement }[] = [
            { id: 'learning', label: 'Lernfortschritt', icon: <SchoolIcon /> },
            { id: 'engagement', label: 'Engagement', icon: <PeopleIcon /> },
            { id: 'assessment', label: 'Assessments', icon: <AssessmentIcon /> },
        ];

        if (isFinanceAllowed) {
            baseTabs.push({ id: 'financial', label: 'Finanzen', icon: <AttachMoneyIcon /> });
        }

        baseTabs.push(
            { id: 'ratings', label: 'Bewertungen', icon: <StarIcon /> },
            { id: 'course', label: 'Kurs-Details', icon: <MenuBookIcon /> },
            { id: 'user', label: 'Nutzer-Details', icon: <PersonIcon /> }
        );

        return baseTabs;
    }, [isFinanceAllowed]);

    const getToken = () => localStorage.getItem('token');

    const customRangeValid = isValidDate(customFrom) && isValidDate(customTo) && customFrom.getTime() <= customTo.getTime();

    useEffect(() => {
        if (!isValidDate(customFrom) || !isValidDate(customTo) || customFrom.getTime() > customTo.getTime()) {
            return;
        }
        const next = { from: toYmd(customFrom), to: toYmd(customTo) };
        const timer = setTimeout(() => {
            setAppliedCustom((prev) => (prev.from === next.from && prev.to === next.to ? prev : next));
        }, 500);
        return () => clearTimeout(timer);
    }, [customFrom, customTo]);

    const formatDe = (ymd: string) => ymd.split('-').reverse().join('.');
    const customPending = customRangeValid
        && (toYmd(customFrom as Date) !== appliedCustom.from || toYmd(customTo as Date) !== appliedCustom.to);

    const dateParams = useMemo(() => {
        if (dateRange === 'custom') {
            return appliedCustom;
        }
        return {
            from: `-${dateRange} days`,
            to: 'now',
        };
    }, [dateRange, appliedCustom]);

    const fetchReport = useCallback(async (reportType: string) => {
        const token = getToken();
        if (!token) return null;

        const { from, to } = dateParams;
        const params = new URLSearchParams({ from, to, groupBy });
        if (companyId) {
            params.append('companyId', companyId.toString());
        }
        if (departmentId) {
            params.append('departmentId', departmentId.toString());
        }
        if (groupId) {
            params.append('groupId', groupId.toString());
        }

        const response = await fetch(`${API_URL}/analytics/reports/${reportType}?${params}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch ${reportType} report`);
        }

        return response.json();
    }, [dateParams, groupBy, companyId, departmentId, groupId]);

    const latestLoad = useRef(0);

    const loadCurrentTab = useCallback(async () => {
        // Only load data for tabs that need server-side fetching
        if (activeTab === 'course' || activeTab === 'user' || activeTab === 'ratings') {
            // These tabs handle their own data loading
            return;
        }

        const loadId = ++latestLoad.current;
        setLoading(true);
        setError(null);

        try {
            switch (activeTab) {
                case 'learning':
                    const learning = await fetchReport('learning-progress');
                    if (loadId !== latestLoad.current) return;
                    setLearningData(learning);
                    break;
                case 'engagement':
                    const engagement = await fetchReport('user-engagement');
                    if (loadId !== latestLoad.current) return;
                    setEngagementData(engagement);
                    break;
                case 'assessment':
                    const assessment = await fetchReport('assessment-performance');
                    if (loadId !== latestLoad.current) return;
                    setAssessmentData(assessment);
                    break;
                case 'financial':
                    const financial = await fetchReport('financial-summary');
                    if (loadId !== latestLoad.current) return;
                    setFinancialData(financial);
                    break;
            }
        } catch (err) {
            if (loadId !== latestLoad.current) return;
            console.error('Error loading report:', err);
            setError('Fehler beim Laden des Reports');
            notify('Fehler beim Laden des Reports', { type: 'error' });
        } finally {
            if (loadId === latestLoad.current) {
                setLoading(false);
            }
        }
    }, [activeTab, fetchReport, notify]);

    useEffect(() => {
        loadCurrentTab();
    }, [loadCurrentTab]);

    const handleExport = async (exportFormat: 'pdf' | 'csv' | 'excel') => {
        const token = getToken();
        if (!token) return;

        const reportTypeMap: Record<TabId, string> = {
            'learning': 'learning-progress',
            'engagement': 'user-engagement',
            'assessment': 'assessment-performance',
            'financial': 'financial-summary',
            'ratings': 'learning-progress', // Fallback
            'course': 'learning-progress', // Fallback
            'user': 'learning-progress', // Fallback
        };
        const reportType = reportTypeMap[activeTab];

        setLoading(true);
        try {
            const { from, to } = dateParams;
            const response = await fetch(`${API_URL}/analytics/reports/export/${exportFormat}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': exportFormat === 'pdf' ? 'application/pdf' :
                             exportFormat === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
                             'text/csv',
                },
                body: JSON.stringify({
                    reportType,
                    filters: { from, to, groupBy, companyId, departmentId, groupId },
                }),
            });

            if (!response.ok) {
                throw new Error('Export failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const ext = exportFormat === 'pdf' ? 'pdf' : exportFormat === 'excel' ? 'xlsx' : 'csv';
            a.download = `report-${reportType}.${ext}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            notify('Export erfolgreich', { type: 'success' });
        } catch (err) {
            console.error('Export error:', err);
            notify('Fehler beim Export', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(tabs[newValue].id);
    };

    const currentTabIndex = tabs.findIndex(t => t.id === activeTab);

    // Check if current tab supports export
    const canExport = ['learning', 'engagement', 'assessment', 'financial'].includes(activeTab);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 2 }}>
                <Title title="Analytics & Reports" />

                {/* flexWrap: Kopfzeile + Export-Buttons duerfen auf schmalen
                    Viewports umbrechen statt die Seite aufzuziehen (#36-Sweep) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h4" component="h1">
                        Analytics & Reports
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={loadCurrentTab}
                            disabled={loading || activeTab === 'course' || activeTab === 'user'}
                        >
                            Aktualisieren
                        </Button>
                        {canExport && (
                            <ButtonGroup variant="outlined" size="small">
                                <Button
                                    startIcon={<DownloadIcon />}
                                    onClick={() => handleExport('pdf')}
                                    disabled={loading}
                                >
                                    PDF
                                </Button>
                                <Button
                                    onClick={() => handleExport('csv')}
                                    disabled={loading}
                                >
                                    CSV
                                </Button>
                                <Button
                                    onClick={() => handleExport('excel')}
                                    disabled={loading}
                                >
                                    Excel
                                </Button>
                            </ButtonGroup>
                        )}
                    </Box>
                </Box>

                {/* Filters */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Zeitraum</InputLabel>
                                <Select
                                    value={dateRange}
                                    label="Zeitraum"
                                    onChange={(e) => setDateRange(e.target.value as DateRange)}
                                >
                                    <MenuItem value="7">Letzte 7 Tage</MenuItem>
                                    <MenuItem value="30">Letzte 30 Tage</MenuItem>
                                    <MenuItem value="90">Letzte 90 Tage</MenuItem>
                                    <MenuItem value="365">Letztes Jahr</MenuItem>
                                    <MenuItem value="custom">Benutzerdefiniert</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {dateRange === 'custom' && (
                            <>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <DatePicker
                                        label="Von"
                                        value={customFrom}
                                        onChange={(date) => setCustomFrom(date)}
                                        maxDate={isValidDate(customTo) ? customTo : undefined}
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <DatePicker
                                        label="Bis"
                                        value={customTo}
                                        onChange={(date) => setCustomTo(date)}
                                        minDate={isValidDate(customFrom) ? customFrom : undefined}
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    />
                                </Grid>
                                {(!customRangeValid || customPending) && (
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {customRangeValid ? 'Neuer Zeitraum wird übernommen' : 'Zeitraum unvollständig oder ungültig'} — angezeigt und exportiert wird weiter {formatDe(appliedCustom.from)} bis {formatDe(appliedCustom.to)}.
                                        </Typography>
                                    </Grid>
                                )}
                            </>
                        )}

                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Gruppierung</InputLabel>
                                <Select
                                    value={groupBy}
                                    label="Gruppierung"
                                    onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
                                >
                                    <MenuItem value="day">Tag</MenuItem>
                                    <MenuItem value="week">Woche</MenuItem>
                                    <MenuItem value="month">Monat</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Company Selector for Operators */}
                        {isOperator && (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <CompanySelector
                                    value={companyId}
                                    onChange={(id) => {
                                        setCompanyId(id);
                                        setDepartmentId(null);
                                        setGroupId(null);
                                    }}
                                    disabled={loading}
                                />
                            </Grid>
                        )}

                        {/* Department Selector */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <DepartmentSelector
                                value={departmentId}
                                onChange={setDepartmentId}
                                companyId={companyId}
                                disabled={loading}
                            />
                        </Grid>

                        {/* Group Selector */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <GroupSelector
                                value={groupId}
                                onChange={setGroupId}
                                companyId={companyId}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Tabs */}
                <Paper>
                    <Tabs
                        value={currentTabIndex}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {tabs.map((tab) => (
                            <Tab key={tab.id} icon={tab.icon} label={tab.label} />
                        ))}
                    </Tabs>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ py: 3 }}>
                            {activeTab === 'learning' && (
                                <LearningProgressDashboard data={learningData} />
                            )}
                            {activeTab === 'engagement' && (
                                <EngagementDashboard data={engagementData} />
                            )}
                            {activeTab === 'assessment' && (
                                <AssessmentDashboard data={assessmentData} />
                            )}
                            {activeTab === 'financial' && isFinanceAllowed && (
                                <FinancialDashboard data={financialData} />
                            )}
                            {activeTab === 'ratings' && (
                                <RatingsDashboard />
                            )}
                            {activeTab === 'course' && (
                                <CourseDetailDashboard
                                    dateParams={dateParams}
                                    groupBy={groupBy}
                                    companyId={companyId}
                                    departmentId={departmentId}
                                    groupId={groupId}
                                />
                            )}
                            {activeTab === 'user' && (
                                <UserDetailDashboard
                                    dateParams={dateParams}
                                    groupBy={groupBy}
                                    companyId={companyId}
                                    departmentId={departmentId}
                                    groupId={groupId}
                                />
                            )}
                        </Box>
                    )}
                </Paper>
            </Box>
        </LocalizationProvider>
    );
};

export default AnalyticsPage;
